// Timbo collaborative bridge — magic-link login gate.
// Wraps <App/>. If auth isn't configured (no Supabase env), renders children
// straight through so nothing breaks in dev/preview. Otherwise: requires a
// signed-in, allow-listed email; stashes { email, role } in localStorage for
// App.js (Performance gating + event attribution).

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { supabase, isAllowed, isOwner } from './auth';

const FONT = 'DM Sans, sans-serif';

function KeyStep({ onDone }) {
  const [k, setK] = useState('');
  const save = (e) => {
    e.preventDefault();
    const clean = k.replace(/\s+/g, '');
    if (clean) { try { localStorage.setItem('el_api_key', clean); } catch {} }
    onDone();
  };
  return (
    <Splash>
      <div style={{ fontSize: 15, fontWeight: 600 }}>Add your ElevenLabs API key</div>
      <div style={{ fontSize: 13, color: '#666', maxWidth: 340, marginBottom: 4 }}>
        Voiceovers run on your own ElevenLabs credit. Your key stays in this browser — it's never shared.
      </div>
      <form onSubmit={save} style={{ display: 'flex', flexDirection: 'column', gap: 10, width: 300 }}>
        <input type="password" value={k} onChange={e => setK(e.target.value)} placeholder="sk_..." autoFocus
          style={{ height: 42, borderRadius: 8, border: '1px solid #e0e0e0', padding: '0 12px', fontSize: 14, fontFamily: FONT, outline: 'none' }} />
        <button type="submit"
          style={{ height: 42, borderRadius: 8, border: 'none', background: '#F97316', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: FONT }}>
          Save & continue
        </button>
      </form>
      <a href="https://elevenlabs.io/app/settings/api-keys" target="_blank" rel="noreferrer"
        style={{ fontSize: 12, color: '#F97316' }}>Where do I find my key? →</a>
      <button onClick={onDone}
        style={{ background: 'none', border: 'none', color: '#999', fontSize: 12, cursor: 'pointer', marginTop: 2, fontFamily: FONT }}>
        Skip for now
      </button>
    </Splash>
  );
}

function Splash({ children }) {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#fff', fontFamily: FONT, color: '#0a0a0a', padding: 24,
    }}>
      <div style={{
        width: 320, maxWidth: '100%', display: 'flex', flexDirection: 'column',
        alignItems: 'center', textAlign: 'center', gap: 12,
      }}>
        <img src={process.env.PUBLIC_URL + '/timbo-logo.png'} alt="Timbo"
          style={{ height: 46, width: 'auto', marginBottom: 6 }} />
        <div style={{ fontSize: 11, color: '#999', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 14 }}>
          Unlimited video remix
        </div>
        {children}
      </div>
    </div>
  );
}

export default function LoginGate({ children }) {
  const [ready, setReady]     = useState(false);
  const [session, setSession] = useState(null);
  const [email, setEmail]     = useState('');
  const [sent, setSent]       = useState(false);
  const [error, setError]     = useState('');
  const [busy, setBusy]       = useState(false);
  const [keyDone, setKeyDone] = useState(false);

  useEffect(() => {
    if (!supabase) { setReady(true); return; }
    // Attach the access token to every backend call so protected endpoints
    // (voiceover / script / remix) accept it.
    const sync = (s) => {
      try {
        if (s && s.access_token) axios.defaults.headers.common['Authorization'] = `Bearer ${s.access_token}`;
        else delete axios.defaults.headers.common['Authorization'];
      } catch {}
    };
    // Un Supabase en pause / injoignable ne doit JAMAIS bloquer le boot sur
    // "Loading…" : getSession() est couru contre un timeout (4s) + un catch,
    // et on dégrade vers l'écran de login plutôt que de figer l'app. C'est
    // exactement la panne silencieuse que ce chantier vise à éliminer.
    let done = false;
    const finish = (s) => { if (done) return; done = true; sync(s); setSession(s); setReady(true); };
    const timer = setTimeout(() => finish(null), 4000);
    supabase.auth.getSession()
      .then(({ data }) => { clearTimeout(timer); finish(data && data.session); })
      .catch(() => { clearTimeout(timer); finish(null); });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => { sync(s); setSession(s); });
    return () => { clearTimeout(timer); try { sub.subscription.unsubscribe(); } catch {} };
  }, []);

  // Auth not configured → run open (no gate).
  if (!supabase) return <>{children}</>;
  if (!ready) return <Splash><div style={{ fontSize: 13, color: '#666' }}>Loading…</div></Splash>;

  if (session) {
    const userEmail = session.user?.email || '';
    if (!isAllowed(userEmail)) {
      return (
        <Splash>
          <div style={{ fontSize: 14, maxWidth: 320 }}>
            <b>{userEmail}</b> isn't invited to this Timbo yet.
          </div>
          <button onClick={() => supabase.auth.signOut()}
            style={{ marginTop: 8, height: 38, padding: '0 16px', borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer', fontFamily: FONT, fontSize: 13 }}>
            Sign out
          </button>
        </Splash>
      );
    }
    const role = isOwner(userEmail) ? 'owner' : 'guest';
    try {
      localStorage.setItem('timbo_user_email', userEmail);
      localStorage.setItem('timbo_user_role', role);
    } catch {}
    // Guests bring their own ElevenLabs key (their own credit). Owner keeps the
    // Railway-injected key, so we never prompt them.
    const hasKey = (() => { try { return !!localStorage.getItem('el_api_key'); } catch { return true; } })();
    if (role === 'guest' && !hasKey && !keyDone) {
      return <KeyStep onDone={() => setKeyDone(true)} />;
    }
    return <>{children}</>;
  }

  const sendLink = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: { emailRedirectTo: window.location.origin },
      });
      if (error) setError(error.message); else setSent(true);
    } catch (err) { setError(err.message || 'Something went wrong'); }
    setBusy(false);
  };

  return (
    <Splash>
      {sent ? (
        <div style={{ fontSize: 14, maxWidth: 300 }}>
          Check your inbox — a sign-in link is on its way to <b>{email}</b>.
          <div style={{ marginTop: 10, fontSize: 12, color: '#999' }}>You can close this tab and open the link.</div>
        </div>
      ) : (
        <form onSubmit={sendLink} style={{ display: 'flex', flexDirection: 'column', gap: 10, width: 280 }}>
          <input type="email" required placeholder="you@company.com" value={email}
            onChange={e => setEmail(e.target.value)} autoFocus
            style={{ height: 42, borderRadius: 8, border: '1px solid #e0e0e0', padding: '0 12px', fontSize: 14, fontFamily: FONT, outline: 'none' }} />
          <button type="submit" disabled={busy}
            style={{ height: 42, borderRadius: 8, border: 'none', background: '#F97316', color: '#fff', fontWeight: 600, fontSize: 14, cursor: busy ? 'default' : 'pointer', fontFamily: FONT, opacity: busy ? 0.7 : 1 }}>
            {busy ? 'Sending…' : 'Send me a sign-in link'}
          </button>
          {error && <div style={{ color: '#dc2626', fontSize: 12 }}>{error}</div>}
        </form>
      )}
    </Splash>
  );
}
